import { Deserializer } from "jsonapi-serializer";
import { ReasonCode } from "../models/ReasonCode";
import { ReasonCodeWrapper } from "../models/wrappers/ReasonCodeWrapper";
import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { ListQuery } from "../models/ListQuery";
import { PaginationModel } from "../models/Pagination";
import { Name } from "../models/Name";
import { APIConnector } from "./api/APIConnector";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { genericError } from "../models/GenericError";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";

const apiConnector = new APIConnector();

export const getReasonCodes = async (listParameters?: ListQuery): Promise<ReasonCodeWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `accessReasonCode${partialURL}`.replace('&', '?'); //HACK: I am not sure if this is a better way of doing this.

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: ReasonCode[] = data.map((reasonCode): ReasonCode => {
                    return {
                        id: reasonCode['id'],
                        name: reasonCode['arc-desc'],
                        slot: reasonCode['id']
                    }
                })

                return resolve({ rows, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReasonCodeById = async (reasonCodeId: string): Promise<ReasonCode> => {
    try {
        const url = `accessReasonCode/${reasonCodeId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            } else {
                new Deserializer().deserialize(json, (error: any, data: any[]) => {

                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reasonCode: ReasonCode = {
                        id: data['id'],
                        name: data['arc-desc'],
                        slot: data['id']

                    }

                    return resolve(reasonCode);
                })
            }

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReasonCodesNameWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `accessReasonCode?fields[accessReasonCode]=arcDesc${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const reasonCode: Name[] = data.map(reasonCode => { return { label: reasonCode['arc-desc'], value: reasonCode['id'] } });
                return resolve({ rows: reasonCode, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeReasonCode = async (reasonCode: ReasonCode, createReasonCode = true) => {
    try {
        const newRecord = {
            data: {
                type: "accessReasonCode",
                id: reasonCode.id,
                attributes: {
                    arcDesc: reasonCode.name
                },
            }
        }

        const url = `accessReasonCode${createReasonCode ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createReasonCode, newRecord).then(data => {
            response = data;
        }).catch(error => {
            console.log('Save reason code error: ', error);
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createReasonCode) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, reasonCode) => {
                        if (error) {
                            return reject({ status: response.status, id: reasonCode.id, success: false, message: genericError });
                        }
                        return resolve({ id: reasonCode.id, success: true });
                    })

                    return;

                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: reasonCode.id, success: true })
                }
                const content = await response.json();
                return reject({ status: response.status, id: reasonCode.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });

            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: reasonCode.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
